import * as React from "react";
import Placeholder from "@warda/library-ui/core/Placeholder";
import { AppCookiesProvider } from "@warda/library-ui/contexts/AppCookiesContext";
import { AppMediaTypeProvider } from "@warda/library-ui/contexts/AppMediaTypeContext/AppMediaTypeContext";
import { ERROR_GENERIC } from "@warda/library-ui/constants";
import SharePageFrame from "./SharePageFrame";
import FormLogin from "./FormLogin";
import SharePage from "./SharePage";
import { AccessStatus, TOKEN_EXPIRED_CHECK_INTERVAL } from "./constants";
import apiUrls from "../../api/apiUrlsSharePage";
import { BASE_URL } from "../../constants/keys";
import { SharePageAuthCookiesHelper } from "./accessCodeUtil/SharePageAuthCookiesHelper";
import { getAccessCodeToken, getPageCode, removeAccessCodeTokenForPageCode, } from "./accessCodeUtil";
import BtnRefresh from "./Buttons/BtnRefresh";
const AreaSharePage = () => {
    const [pageCode, setPageCode] = React.useState(getPageCode());
    const [isAuthRequired, setAuthRequired] = React.useState(!!getAccessCodeToken());
    const [accessStatus, setAccessStatus] = React.useState(getAccessCodeToken() ? AccessStatus.GRANTED : AccessStatus.VERIFYING);
    const recheckLoginRequired = React.useCallback(() => {
        if (isAuthRequired) {
            const hasToken = !!getAccessCodeToken();
            if (!hasToken)
                setAccessStatus(AccessStatus.LOGIN_REQUIRED);
        }
    }, [isAuthRequired]);
    React.useEffect(() => {
        if (pageCode !== getPageCode()) {
            const accessCodeToken = getAccessCodeToken();
            setPageCode(getPageCode());
            setAuthRequired(!!accessCodeToken);
            setAccessStatus(accessCodeToken ? AccessStatus.GRANTED : AccessStatus.VERIFYING);
        }
        recheckLoginRequired();
    }, [pageCode, recheckLoginRequired]);
    React.useEffect(() => {
        const interval = setInterval(recheckLoginRequired, TOKEN_EXPIRED_CHECK_INTERVAL);
        return () => clearInterval(interval);
    }, [recheckLoginRequired]);
    React.useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                recheckLoginRequired();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [recheckLoginRequired]);
    React.useEffect(() => {
        recheckLoginRequired();
    }, [pageCode, recheckLoginRequired]);
    const authCookiesHelper = React.useMemo(() => SharePageAuthCookiesHelper({ pageCode, recheckLoginRequired }), [pageCode, recheckLoginRequired]);
    const handleError = React.useCallback((error) => {
        switch (error.status) {
            case 401:
                removeAccessCodeTokenForPageCode(pageCode);
                setAuthRequired(true);
                setAccessStatus(AccessStatus.LOGIN_REQUIRED);
                recheckLoginRequired();
                break;
            case 403:
                removeAccessCodeTokenForPageCode(pageCode);
                setAccessStatus(AccessStatus.FORBIDDEN);
                break;
        }
    }, [pageCode, recheckLoginRequired]);
    const handleSerchResult = React.useCallback((status) => {
        if (!status) {
            const accessCodeToken = getAccessCodeToken();
            setAuthRequired(!!accessCodeToken);
            setAccessStatus(AccessStatus.GRANTED);
            recheckLoginRequired();
        }
        else
            switch (status) {
                case 401:
                    removeAccessCodeTokenForPageCode(pageCode);
                    setAuthRequired(true);
                    setAccessStatus(AccessStatus.LOGIN_REQUIRED);
                    recheckLoginRequired();
                    break;
                case 403:
                    removeAccessCodeTokenForPageCode(pageCode);
                    setAccessStatus(AccessStatus.FORBIDDEN);
                    break;
                case 500:
                    setAccessStatus(AccessStatus.SYSTEM_ERROR);
                    break;
            }
    }, [pageCode, recheckLoginRequired]);
    const getMediaTypeHelper = React.useCallback(() => {
        const { url, method } = apiUrls.fileTypesSynoptic;
        return {
            mediaApiUrl: { url, method },
            baseUrl: BASE_URL,
            authCookiesHelper,
        };
    }, [authCookiesHelper]);
    const handleLoginSuccess = React.useCallback(() => {
        setAccessStatus(AccessStatus.GRANTED);
    }, []);
    switch (accessStatus) {
        case AccessStatus.LOGIN_REQUIRED:
            return (React.createElement(SharePageFrame, { content: React.createElement(FormLogin, { onLoginSuccess: handleLoginSuccess }) }));
        case AccessStatus.VERIFYING:
        case AccessStatus.GRANTED:
            return isAuthRequired ? (React.createElement(AppCookiesProvider, { authCookiesHelper: authCookiesHelper },
                React.createElement(AppMediaTypeProvider, { mediaTypeHelper: getMediaTypeHelper() },
                    React.createElement(SharePage, { handleError: handleError, handleSerchResult: handleSerchResult, accessStatus: accessStatus })))) : (React.createElement(AppMediaTypeProvider, { mediaTypeHelper: getMediaTypeHelper(), onError: handleError },
                React.createElement(SharePage, { handleError: handleError, handleSerchResult: handleSerchResult, accessStatus: accessStatus })));
        case AccessStatus.SYSTEM_ERROR:
            return React.createElement(SharePageError, { icon: "error_outline", label: ERROR_GENERIC });
        case AccessStatus.FORBIDDEN:
            return (React.createElement(SharePageError, { icon: "live_help", label: "You can't access this page" }));
    }
};
const SharePageError = ({ icon, label }) => {
    return (React.createElement(SharePageFrame, { innerToolbar: React.createElement(React.Fragment, null,
            React.createElement("div", { style: { flex: 1 } }),
            React.createElement(BtnRefresh, null)), content: React.createElement(Placeholder, { icon: icon, label: label }) }));
};
export default AreaSharePage;
